import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fixImageUrl } from '../../lib/utils';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiUser, FiStar, FiX, FiBookOpen, FiImage, FiClock, FiCoffee, FiChevronRight } = FiIcons;

const Modal = ({ title, icon, onClose, children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={onClose}>
    <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[3rem] shadow-2xl relative" onClick={e => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-6 right-6 p-3 bg-stone-100 hover:bg-stone-200 text-stone-500 rounded-full transition-all z-10">
        <SafeIcon icon={FiX} className="w-5 h-5" />
      </button>
      {children}
    </motion.div>
  </motion.div>
);

const PersonModal = ({ person, onClose }) => {
  const [gallery, setGallery] = useState([]);
  useEffect(() => {
    const fetchGallery = async () => {
      const { data } = await supabase.from('person_media_20240520').select('*').eq('person_id', person.id).order('sort_order');
      setGallery(data || []);
    };
    fetchGallery();
  }, [person.id]);

  return (
    <Modal onClose={onClose}>
      <div className="relative h-72 sm:h-96 bg-stone-100">
        <img src={fixImageUrl(person.photo_url)} className="w-full h-full object-cover" alt={person.name} />
      </div>
      <div className="p-8 sm:p-14">
        <div className="mb-10 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-[#8c7851] bg-[#8c7851]/10 px-4 py-1.5 rounded-full">{person.relation}</span>
          <h2 className="text-5xl font-serif font-bold mt-4 text-[#2c2c2c]">{person.name}</h2>
          <p className="text-[#8c7851] font-medium mt-2 capitalize">{person.side_of_family} Family Branch</p>
        </div>
        <div className="space-y-12">
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiBookOpen} className="text-[#8c7851]" /> Biography & Story
            </h3>
            <p className="text-xl leading-relaxed text-stone-600 whitespace-pre-wrap font-serif italic">"{person.bio || "Story coming soon..."}"</p>
          </section>
          {person.favorite_things && (
            <section className="bg-stone-50 p-8 rounded-[2rem] border border-stone-100">
              <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiStar} className="text-amber-500" /> Favorite Things
              </h3>
              <p className="text-stone-700 leading-relaxed">{person.favorite_things}</p>
            </section>
          )}
          {gallery.length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-6 flex items-center gap-2">
                <SafeIcon icon={FiImage} className="text-[#8c7851]" /> Memories & Photos
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {gallery.map((img, idx) => (
                  <div key={idx} className="rounded-2xl overflow-hidden aspect-square border-2 border-white shadow-sm">
                    <img src={fixImageUrl(img.url)} className="w-full h-full object-cover" alt="Gallery" />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </Modal>
  );
};

const RecipeModal = ({ recipe, onClose }) => (
  <Modal onClose={onClose}>
    <div className="p-10 sm:p-14">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
          <SafeIcon icon={FiCoffee} className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-4xl font-serif font-bold text-[#2c2c2c]">{recipe.title}</h2>
          <p className="text-[#8c7851] font-bold text-xs uppercase tracking-widest mt-1">Shared by {recipe.submitted_by}</p>
        </div>
      </div>
      <div className="space-y-10">
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">The Ingredients</h3>
          <div className="bg-stone-50 p-8 rounded-3xl border border-stone-100 text-stone-700 leading-relaxed whitespace-pre-wrap font-medium">
            {recipe.ingredients}
          </div>
        </section>
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">Instructions</h3>
          <p className="text-xl font-serif text-[#2c2c2c] leading-relaxed whitespace-pre-wrap">
            {recipe.instructions}
          </p>
        </section>
      </div>
    </div>
  </Modal>
);

const TraditionModal = ({ tradition, onClose }) => (
  <Modal onClose={onClose}>
    {tradition.image_url && (
      <div className="h-64 sm:h-80 w-full overflow-hidden">
        <img src={fixImageUrl(tradition.image_url)} className="w-full h-full object-cover" alt={tradition.title} />
      </div>
    )}
    <div className="p-10 sm:p-14">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
          <SafeIcon icon={FiClock} className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-4xl font-serif font-bold text-[#2c2c2c]">{tradition.title}</h2>
          <span className="text-[#8c7851] font-bold text-xs uppercase tracking-widest bg-stone-50 px-3 py-1 rounded-full">{tradition.occurrence}</span>
        </div>
      </div>
      <div className="prose prose-stone max-w-none">
        <p className="text-xl font-serif text-[#2c2c2c] leading-relaxed whitespace-pre-wrap">
          {tradition.description}
        </p>
      </div>
    </div>
  </Modal>
);

const FamilyTree = () => {
  const [people, setPeople] = useState([]);
  const [traditions, setTraditions] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedTradition, setSelectedTradition] = useState(null);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [ppl, trad, recp] = await Promise.all([
      supabase.from('people_20240520').select('*').order('name'),
      supabase.from('traditions_20240520').select('*').order('created_at'),
      supabase.from('recipes_20240520').select('*').order('title')
    ]);
    setPeople(ppl.data || []);
    setTraditions(trad.data || []);
    setRecipes(recp.data || []);
    setLoading(false);
  };

  const Branch = ({ title, members }) => (
    <div className="mb-16">
      <h3 className="text-xl font-serif font-bold mb-8 flex items-center gap-4">
        <span className="h-px bg-stone-200 flex-1"></span>
        <span className="text-[#8c7851] whitespace-nowrap uppercase tracking-widest text-xs font-bold">{title}</span>
        <span className="h-px bg-stone-200 flex-1"></span>
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map(person => (
          <button key={person.id} onClick={() => setSelectedPerson(person)} className="group bg-white p-6 rounded-[2.5rem] border border-stone-100 flex items-center gap-6 text-left hover:shadow-xl hover:border-[#8c7851]/30 transition-all active:scale-[0.98]">
            <div className="w-20 h-20 rounded-3xl overflow-hidden bg-stone-50 flex-shrink-0 shadow-inner">
              <img src={fixImageUrl(person.photo_url)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt={person.name} />
            </div>
            <div>
              <h4 className="font-bold text-xl text-[#2c2c2c] group-hover:text-[#8c7851] transition-colors">{person.name}</h4>
              <p className="text-xs font-bold text-[#8c7851] uppercase tracking-widest mt-1 opacity-70">{person.relation}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="py-8">
      <div className="flex justify-center gap-2 mb-16 p-2 bg-stone-100 w-fit mx-auto rounded-[1.5rem] shadow-inner">
        {[
          { id: 'members', label: 'Members', icon: FiUser },
          { id: 'traditions', label: 'Traditions', icon: FiClock },
          { id: 'recipes', label: 'Recipes', icon: FiCoffee }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-8 py-3 rounded-2xl text-sm font-bold transition-all ${activeTab === tab.id ? 'bg-white text-[#8c7851] shadow-md' : 'text-stone-500 hover:text-stone-800'}`} >
            <SafeIcon icon={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'members' && (
          <motion.div key="members" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Branch title="Maternal Side" members={people.filter(p => p.side_of_family === 'maternal')} />
            <Branch title="Paternal Side" members={people.filter(p => p.side_of_family === 'paternal')} />
          </motion.div>
        )}

        {activeTab === 'traditions' && (
          <motion.div key="traditions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {traditions.map(t => (
              <button key={t.id} onClick={() => setSelectedTradition(t)} className="bg-white text-left rounded-[3rem] overflow-hidden border border-stone-100 shadow-sm hover:shadow-xl transition-all group">
                {t.image_url && <img src={fixImageUrl(t.image_url)} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700" alt={t.title} />}
                <div className="p-10">
                  <span className="text-[10px] font-bold text-[#8c7851] uppercase tracking-widest bg-stone-50 px-3 py-1 rounded-full">{t.occurrence}</span>
                  <h3 className="text-3xl font-serif font-bold mt-4 mb-4 group-hover:text-[#8c7851] transition-colors">{t.title}</h3>
                  <p className="text-stone-600 leading-relaxed line-clamp-3 italic">"{t.description?.substring(0, 150)}..."</p>
                  <div className="mt-6 flex items-center text-xs font-bold text-[#8c7851] gap-1 group-hover:gap-2 transition-all">
                    Read the whole story <SafeIcon icon={FiChevronRight} />
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}

        {activeTab === 'recipes' && (
          <motion.div key="recipes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recipes.map(r => (
              <button key={r.id} onClick={() => setSelectedRecipe(r)} className="bg-[#fdfcf9] text-left p-10 rounded-[3rem] border border-stone-200 shadow-inner group transition-all hover:bg-white hover:shadow-xl">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 className="text-3xl font-serif font-bold group-hover:text-[#8c7851] transition-colors">{r.title}</h3>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mt-2">Shared by: {r.submitted_by}</p>
                  </div>
                  <div className="w-14 h-14 bg-white rounded-3xl flex items-center justify-center text-[#8c7851] shadow-sm">
                    <SafeIcon icon={FiCoffee} className="w-6 h-6" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-xs font-bold text-[#8c7851] gap-1 group-hover:gap-2 transition-all">
                  View Full Recipe <SafeIcon icon={FiChevronRight} />
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedPerson && <PersonModal person={selectedPerson} onClose={() => setSelectedPerson(null)} />}
        {selectedRecipe && <RecipeModal recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} />}
        {selectedTradition && <TraditionModal tradition={selectedTradition} onClose={() => setSelectedTradition(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default FamilyTree;